import { z } from "zod";
import { projectRoleSchema } from "./project";

/**
 * The lifecycle of an invitation. `accepted` is terminal and idempotent: accepting twice lands on
 * the same membership. `revoked` ends the token early, and `expired` is reached after 7 days.
 */
export const invitationStatuses = ["pending", "accepted", "revoked", "expired"] as const;
export const invitationStatusSchema = z.enum(invitationStatuses);
export type InvitationStatus = z.infer<typeof invitationStatusSchema>;

/**
 * One invitation, as the API returns it. The token itself is never part of this: it travels only
 * in the invite link. `invitedBy` is the display name of the person who sent it.
 */
export const invitationSchema = z.object({
  id: z.uuid(),
  projectId: z.uuid(),
  projectName: z.string().min(1),
  email: z.email(),
  role: projectRoleSchema,
  status: invitationStatusSchema,
  invitedBy: z.string().min(1),
  expiresAt: z.iso.datetime(),
});
export type Invitation = z.infer<typeof invitationSchema>;

/** `GET /api/v1/projects/:id/invitations`: the pending invitations, newest first. */
export const invitationListSchema = z.array(invitationSchema);
export type InvitationList = z.infer<typeof invitationListSchema>;

/** An invitation lasts 7 days. A resend issues a new token and expires the old one. */
export const INVITATION_TTL_DAYS = 7;
